import React, { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import { useDialog } from "../../lib/dialog";
import { Bell, Plus, Send, Check, X, CalendarDays, Users, BellRing, ChevronRight, Trash2 } from "lucide-react";

const TYPE_BADGE = {
  standard: { label: "Standard", cls: "bg-[#262626] text-[#A19D94]" },
  attendance: { label: "Attendance", cls: "bg-blood/15 text-blood border border-blood/40" },
};

export default function Notifications() {
  const { formatLong } = useSettings();
  const dialog = useDialog();
  const [items, setItems] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => adminApi.get("/admin/notifications").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const openDetail = async (nid) => {
    const { data } = await adminApi.get(`/admin/notifications/${nid}`);
    setDetail(data);
  };

  if (!items) return <AdminLayout title="Notifications"><Loader /></AdminLayout>;

  return (
    <AdminLayout title="Notifications" subtitle="Send messages and attendance requests to your cast"
      actions={<button data-testid="new-notification-btn" onClick={() => setShowBuilder(true)} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Plus size={14} /> New Notification</button>}>
      {items.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Bell size={32} className="mx-auto text-[#3a3835] mb-3" />
            <p className="text-[#A19D94]">No notifications sent yet.</p>
            <p className="text-sm text-[#6B6862] mt-1">Create one to message your contracted actors or request attendance.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((n) => (
            <button key={n.id} data-testid={`notification-card-${n.id}`} onClick={() => openDetail(n.id)}
              className="w-full text-left border border-[#1a1a1a] bg-[#0A0A0A] p-4 hover:border-[#262626] transition-colors flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${TYPE_BADGE[n.type].cls}`}>{TYPE_BADGE[n.type].label}</span>
                  <span className="font-display uppercase tracking-tight truncate">{n.title}</span>
                </div>
                <div className="text-xs text-[#6B6862] mt-1.5 flex items-center gap-4">
                  <span className="flex items-center gap-1"><Users size={12} /> {n.recipient_count} recipient{n.recipient_count !== 1 ? "s" : ""}</span>
                  {n.type === "attendance" && <span className="flex items-center gap-1 text-[#22C55E]"><Check size={12} /> {n.confirmed_count || 0} confirmed</span>}
                  <span>{formatLong(n.sent_at)}</span>
                </div>
              </div>
              <ChevronRight size={16} className="text-[#6B6862] shrink-0" />
            </button>
          ))}
        </div>
      )}

      {showBuilder && <BuilderModal onClose={() => setShowBuilder(false)} onSent={() => { setShowBuilder(false); load(); }} />}
      {detail && <DetailModal notification={detail} onClose={() => setDetail(null)} onReminded={load} formatLong={formatLong} dialog={dialog} />}
    </AdminLayout>
  );
}

function BuilderModal({ onClose, onSent }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [bodyType, setBodyType] = useState("text"); // "text" | "html"
  const [htmlBody, setHtmlBody] = useState("");
  const [type, setType] = useState("standard");
  const [dateConfig, setDateConfig] = useState("single");
  const [dates, setDates] = useState([{ name: "", date: "", start_time: "", end_time: "", location: "" }]);
  const [mode, setMode] = useState("all_contracted");
  const [roles, setRoles] = useState([]);
  const [roleIds, setRoleIds] = useState([]);
  const [actors, setActors] = useState([]);
  const [actorIds, setActorIds] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/roles").then(({ data }) => setRoles(data.items || data || []));
    adminApi.get("/admin/applicants?status=contracted&limit=500").then(({ data }) => setActors(data.items || []));
  }, []);

  const updDate = (i, k, v) => setDates((arr) => arr.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDate = () => setDates((arr) => [...arr, { name: "", date: "", start_time: "", end_time: "", location: "" }]);
  const removeDate = (i) => setDates((arr) => arr.filter((_, idx) => idx !== i));

  const send = async () => {
    setError(null);
    if (!title.trim()) { setError("Title is required."); return; }
    if (bodyType === "html" ? !htmlBody.trim() : !message.trim()) {
      setError(bodyType === "html" ? "Paste the HTML email content." : "Message is required."); return;
    }
    const payload = {
      title: title.trim(), message: message.trim(),
      body_type: bodyType, html_body: bodyType === "html" ? htmlBody : "",
      type,
      date_config: type === "attendance" ? dateConfig : null,
      dates: type === "attendance" ? (dateConfig === "single" ? dates.slice(0, 1) : dates).filter((d) => d.date) : [],
      recipients_mode: mode,
      role_ids: mode === "roles" ? roleIds : [],
      applicant_ids: mode === "manual" ? actorIds : [],
    };
    if (type === "attendance" && payload.dates.length === 0) { setError("Add at least one date for an attendance request."); return; }
    if (mode === "roles" && roleIds.length === 0) { setError("Select at least one role."); return; }
    if (mode === "manual" && actorIds.length === 0) { setError("Select at least one actor."); return; }
    setSending(true);
    try {
      const { data } = await adminApi.post("/admin/notifications", payload);
      onSent(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(false); }
  };

  const filteredActors = actors.filter((a) => (a.full_name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !sending && onClose()} data-testid="notification-builder">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#1a1a1a] flex items-center justify-between">
          <h3 className="font-display text-xl uppercase tracking-tight">New Notification</h3>
          <button onClick={onClose} className="text-[#6B6862] hover:text-blood"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-mini block mb-1.5">Title</label>
            <input data-testid="notif-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
          </div>
          <div>
            <div className="flex gap-2 mb-2">
              {[["text", "Message"], ["html", "HTML Email"]].map(([v, lbl]) => (
                <button key={v} data-testid={`notif-bodytype-${v}`} onClick={() => setBodyType(v)}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${bodyType === v ? "border-blood bg-blood/5 text-blood" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>{lbl}</button>
              ))}
            </div>
            {bodyType === "text" ? (
              <>
                <label className="label-mini block mb-1.5">Message</label>
                <textarea data-testid="notif-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood resize-none" />
              </>
            ) : (
              <>
                <label className="label-mini block mb-1.5">HTML Email Content</label>
                <textarea data-testid="notif-html-body" rows={10} value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)} placeholder="Paste your full HTML email here…" spellCheck={false} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-xs font-mono focus:outline-none focus:border-blood resize-y" />
                <p className="text-[10px] text-[#6B6862] mt-1">Pasted HTML is sent as-is as the email body. The <strong>Title</strong> above is used as the email subject. Merge variables such as <span className="font-mono">{`{{first_name}}`}</span>, <span className="font-mono">{`{{org_name}}`}</span> are supported.</p>
              </>
            )}
          </div>

          <div>
            <label className="label-mini block mb-2">Type</label>
            <div className="flex gap-2">
              {[["standard", "Standard message"], ["attendance", "Attendance request"]].map(([v, lbl]) => (
                <button key={v} data-testid={`notif-type-${v}`} onClick={() => setType(v)} className={`flex-1 px-3 py-2.5 text-sm border transition-colors ${type === v ? "border-blood bg-blood/5 text-blood" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>{lbl}</button>
              ))}
            </div>
          </div>

          {type === "attendance" && (
            <div className="border border-[#1a1a1a] p-4 space-y-3">
              <div className="flex gap-2">
                {[["single", "One date"], ["multiple", "Multiple — actor picks one"]].map(([v, lbl]) => (
                  <button key={v} data-testid={`notif-dateconfig-${v}`} onClick={() => setDateConfig(v)} className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider border ${dateConfig === v ? "border-blood text-blood" : "border-[#262626] text-[#A19D94]"}`}>{lbl}</button>
                ))}
              </div>
              {(dateConfig === "single" ? dates.slice(0, 1) : dates).map((d, i) => (
                <div key={i} className="border border-[#1a1a1a] p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="label-mini">Date {dateConfig === "multiple" ? i + 1 : ""}</span>
                    {dateConfig === "multiple" && dates.length > 1 && <button onClick={() => removeDate(i)} className="text-[#6B6862] hover:text-blood"><Trash2 size={13} /></button>}
                  </div>
                  <input data-testid={`notif-date-name-${i}`} placeholder="Name (e.g. Dress Rehearsal)" value={d.name} onChange={(e) => updDate(i, "name", e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood" />
                  <div className="grid grid-cols-3 gap-2">
                    <input type="date" data-testid={`notif-date-date-${i}`} value={d.date} onChange={(e) => updDate(i, "date", e.target.value)} className="bg-[#050505] border border-[#262626] px-2 py-2 text-sm focus:outline-none focus:border-blood" />
                    <input type="time" value={d.start_time} onChange={(e) => updDate(i, "start_time", e.target.value)} className="bg-[#050505] border border-[#262626] px-2 py-2 text-sm focus:outline-none focus:border-blood" />
                    <input type="time" value={d.end_time} onChange={(e) => updDate(i, "end_time", e.target.value)} className="bg-[#050505] border border-[#262626] px-2 py-2 text-sm focus:outline-none focus:border-blood" />
                  </div>
                  <input placeholder="Location" value={d.location} onChange={(e) => updDate(i, "location", e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood" />
                </div>
              ))}
              {dateConfig === "multiple" && <button onClick={addDate} className="text-xs uppercase tracking-wider text-blood flex items-center gap-1"><Plus size={12} /> Add another date</button>}
            </div>
          )}

          <div>
            <label className="label-mini block mb-2">Recipients</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {[["all_contracted", "All contracted"], ["cast", "Cast only"], ["roles", "By role"], ["manual", "Pick actors"]].map(([v, lbl]) => (
                <button key={v} data-testid={`notif-recipients-${v}`} onClick={() => setMode(v)} className={`flex-1 min-w-[110px] px-3 py-2 text-xs uppercase tracking-wider border ${mode === v ? "border-blood text-blood" : "border-[#262626] text-[#A19D94]"}`}>{lbl}</button>
              ))}
            </div>
            {mode === "roles" && (
              <div className="border border-[#1a1a1a] p-3 max-h-40 overflow-y-auto space-y-1">
                {roles.length === 0 && <p className="text-xs text-[#6B6862]">No roles created yet.</p>}
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                    <input type="checkbox" data-testid={`notif-role-${r.id}`} checked={roleIds.includes(r.id)} onChange={() => setRoleIds((s) => s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id])} />
                    {r.name}
                  </label>
                ))}
              </div>
            )}
            {mode === "manual" && (
              <div className="border border-[#1a1a1a] p-3">
                <input placeholder="Search actors…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm mb-2 focus:outline-none focus:border-blood" />
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {filteredActors.length === 0 && <p className="text-xs text-[#6B6862]">No contracted actors found.</p>}
                  {filteredActors.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                      <input type="checkbox" data-testid={`notif-actor-${a.id}`} checked={actorIds.includes(a.id)} onChange={() => setActorIds((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id])} />
                      {a.full_name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {mode === "all_contracted" && <p className="text-xs text-[#6B6862]">Sends to every contracted actor.</p>}
            {mode === "cast" && <p className="text-xs text-[#6B6862]">Sends only to cast members — actors with both an assigned role and an accepted contract.</p>}
          </div>

          {error && <p className="text-sm text-blood" data-testid="notif-error">{error}</p>}
        </div>
        <div className="p-5 border-t border-[#1a1a1a] flex justify-end gap-2">
          <button onClick={onClose} disabled={sending} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#3a3835] disabled:opacity-50">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="notif-send-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-50 flex items-center gap-2">
            <Send size={13} /> {sending ? "Sending…" : "Send Notification"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ notification, onClose, onReminded, formatLong, dialog }) {
  const [reminding, setReminding] = useState(false);
  const n = notification;
  const confirmedCount = n.recipients.filter((r) => r.confirmed).length;
  const readCount = n.recipients.filter((r) => r.read).length;

  const remind = async () => {
    const ok = await dialog.confirm({ title: "Send reminder?", description: n.type === "attendance" ? "Re-send this notification to everyone who hasn't confirmed yet." : "Re-send this notification to everyone who hasn't read it yet.", confirmLabel: "Send Reminder" });
    if (!ok) return;
    setReminding(true);
    try {
      const { data } = await adminApi.post(`/admin/notifications/${n.id}/remind`);
      await dialog.alert({ title: "Reminder sent", description: `Re-sent to ${data.count} actor(s).` });
      onReminded();
    } finally { setReminding(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="notification-detail">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#1a1a1a] flex items-start justify-between gap-4">
          <div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${TYPE_BADGE[n.type].cls}`}>{TYPE_BADGE[n.type].label}</span>
            <h3 className="font-display text-xl uppercase tracking-tight mt-2">{n.title}</h3>
            <p className="text-xs text-[#6B6862] mt-1">Sent {formatLong(n.sent_at)}</p>
          </div>
          <button onClick={onClose} className="text-[#6B6862] hover:text-blood shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-[#A19D94] whitespace-pre-wrap">{n.message}</p>

          {n.type === "attendance" && n.dates?.length > 0 && (
            <div className="border border-[#1a1a1a] p-3">
              <div className="label-mini mb-2 flex items-center gap-1"><CalendarDays size={12} /> Date{n.dates.length > 1 ? "s" : ""}</div>
              {n.dates.map((d) => (
                <div key={d.id} className="text-sm text-[#A19D94]">{d.name && <strong className="text-[#F2EBE1]">{d.name} — </strong>}{formatLong(d.date)} {d.start_time && `· ${d.start_time}–${d.end_time}`} {d.location && `· ${d.location}`}</div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 text-xs">
            <span className="text-[#A19D94]"><strong className="text-[#F2EBE1]">{n.recipients.length}</strong> recipients</span>
            <span className="text-[#A19D94]"><strong className="text-[#F2EBE1]">{readCount}</strong> read</span>
            {n.type === "attendance" && <span className="text-[#22C55E]"><strong>{confirmedCount}</strong> confirmed</span>}
          </div>

          <div className="border border-[#1a1a1a]">
            <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider text-[#6B6862] border-b border-[#1a1a1a]">
              <div className="col-span-5">Actor</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-2">Read</div>
              <div className="col-span-2">{n.type === "attendance" ? "Confirmed" : ""}</div>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {n.recipients.map((r) => (
                <div key={r.applicant_id} className="grid grid-cols-12 px-3 py-2 text-sm border-b border-[#121212] last:border-0" data-testid={`notif-recipient-${r.applicant_id}`}>
                  <div className="col-span-5 truncate">{r.full_name}</div>
                  <div className="col-span-3 text-[#6B6862] truncate">{r.role || "—"}</div>
                  <div className="col-span-2">{r.read ? <Check size={14} className="text-[#22C55E]" /> : <span className="text-[#6B6862]">—</span>}</div>
                  <div className="col-span-2 text-xs">
                    {n.type === "attendance"
                      ? (r.confirmed ? <span className="text-[#22C55E]">{r.selected_date ? r.selected_date.name || formatLong(r.selected_date.date) : "Yes"}</span> : <span className="text-[#F59E0B]">Pending</span>)
                      : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-[#1a1a1a] flex justify-end">
          <button onClick={remind} disabled={reminding} data-testid="notif-remind-btn" className="px-4 py-2 text-xs uppercase tracking-wider border border-blood text-blood hover:bg-blood/10 disabled:opacity-50 flex items-center gap-2">
            <BellRing size={13} /> {reminding ? "Sending…" : (n.type === "attendance" ? "Remind unconfirmed" : "Remind unread")}
          </button>
        </div>
      </div>
    </div>
  );
}
